import { createColumnHelper } from "@tanstack/react-table";
import _startCase from "lodash/startCase";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { CustomerCard, ReactTable, TableDropdownSelector } from "components";
import { useRegisterNavAction } from "hooks";
import { LEASE_OPTIONS, useLeaseCompanyOptions } from "modules/LeadsDashboard/hooks";
import "modules/LeadsDashboard/Leads/CarsLeads/components/CarsLeadsTable/CarsLeadsTable.scss";
import { useCarsTableFiltersHandlers, useLeadsCarsFilters, useLeadsCarsQuery } from "modules/LeadsDashboard/Leads/CarsLeads/hooks";
import { getFormattedDate, getLocationName } from "modules/LeadsDashboard/util";
import { PATHS } from "router/paths";

import type { Appointment, DealerLocation } from "models";

type CarsLeadsTableProps = {
  locations: DealerLocation[];
};

const columnHelper =
  createColumnHelper<
    Pick<
      Appointment,
      | "id"
      | "time_car_app"
      | "wo_nr"
      | "car_id"
      | "is_lease_company"
      | "reg_number"
      | "dealer_location_id"
      | "current_km"
      | "next_km"
      | "next_date"
      | "predicted_next_date"
      | "customer_contract"
      | "customer_driver"
      | "customer_owner"
      | "driver_opted_out_from_importing"
      | "owner_opted_out_from_importing"
      | "contractor_opted_out_from_importing"
    >
  >();

export const CarsLeadsTable = ({ locations }: CarsLeadsTableProps) => {
  const { t } = useTranslation();

  const { data: leadsCars, isFetching: isLeadsCarsFetching, refetch: refetchLeadsCars } = useLeadsCarsQuery();
  const { leaseCompanyOptions } = useLeaseCompanyOptions();
  const { date_from, date_to, is_lease_company, page: currentPage, page_size: pageSize, dealer_ids, location_ids } = useLeadsCarsFilters();
  const { handleOnLeaseSelect, handlePaginationSelect, handlePageSizeChange } = useCarsTableFiltersHandlers();

  useRegisterNavAction(() => (dealer_ids.length > 0 || location_ids.length > 0) && refetchLeadsCars());

  const selectedLeaseCompanyOption = is_lease_company === null ? LEASE_OPTIONS.All : is_lease_company ? LEASE_OPTIONS.Lease : LEASE_OPTIONS.NoLease;
  const nbPages = leadsCars?.nb_pages || 1;
  const nbItems = leadsCars?.nb_items || 0;
  const hasDateRangeSelected = date_from && date_to;

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "appointment_date",
        header: () => <span className="no-wrap-text table-header-text">{t("v8_appointment_date", "Appointment Date")} </span>,
        size: 160,
        cell: ({ row: { original } }) => <span className="ellipsis ellipsis-wo-nr">{getFormattedDate(original.time_car_app)}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "wo",
        header: () => <span className="no-wrap-text table-header-text">WO # {nbItems}</span>,
        size: 50,
        cell: ({ row: { original } }) => (
          <p className="ellipsis">
            <Link to={`${PATHS.APPOINTMENTS}/${original.id}`}>
              <Icon className="arrow up right from square" />
              {original.wo_nr}
            </Link>
          </p>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "reg_number",
        header: () => (
          <div className="appointment-table-wrapper">
            <TableDropdownSelector
              options={leaseCompanyOptions}
              multiple={false}
              withSearch={false}
              selected={[selectedLeaseCompanyOption]}
              handleOnSelect={handleOnLeaseSelect}
              trigger={<span className="ellipsis ellipsis-wo-nr table-header-text"> {t("v8_reg_number", "Reg #")}</span>}
            />
          </div>
        ),
        size: 50,
        cell: ({
          row: {
            original: { car_id, is_lease_company, reg_number }
          }
        }) => {
          if (!reg_number) return "";

          return (
            <p className="ellipsis ">
              <Link to={`${PATHS.CAR_DETAIL}/${car_id}`}>
                <Icon className="arrow up right from square" />
                {reg_number}
                {is_lease_company && <Icon name="building" />}
              </Link>
            </p>
          );
        }
      }),

      columnHelper.accessor(row => row, {
        id: "location_name",
        header: () => <span className="no-wrap-text table-header-text">{_startCase(t("v8_location", "Location"))}</span>,
        size: 135,
        cell: ({
          row: {
            original: { dealer_location_id }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getLocationName(dealer_location_id, locations)}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "current_km",
        header: () => <span className="no-wrap-text table-header-text">{_startCase(t("v8_current_km", "Current Km"))} </span>,
        size: 90,
        cell: ({
          row: {
            original: { current_km }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{current_km}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "next_km",
        header: () => <span className="no-wrap-text table-header-text"> {_startCase(t("v8_next_km", "Next Km"))}</span>,
        size: 70,
        cell: ({
          row: {
            original: { next_km }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{next_km}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "next_date",
        header: () => <span className="no-wrap-text table-header-text"> {_startCase(t("v8_next_date", "Next Date"))} </span>,
        size: 90,
        cell: ({
          row: {
            original: { next_date }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getFormattedDate(next_date)}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "predicted_next_date",
        header: () => <span className="no-wrap-text table-header-text"> {_startCase(t("v8_predicted_next_date", "Predicted Next Date"))} </span>,
        size: 235,
        cell: ({
          row: {
            original: { predicted_next_date }
          }
        }) => <p className="ellipsis ellipsis-wo-nr">{getFormattedDate(predicted_next_date)}</p>
      }),

      columnHelper.accessor(row => row, {
        id: "status",
        header: () => null,
        size: 30,
        cell: ({
          row: {
            original: {
              customer_contract,
              customer_driver,
              customer_owner,
              driver_opted_out_from_importing,
              owner_opted_out_from_importing,
              contractor_opted_out_from_importing
            }
          }
        }) => (
          <div className="car-leads-table-customer-card-icon">
            <CustomerCard
              contractor={customer_contract}
              driver={customer_driver}
              owner={customer_owner}
              driverOptedOutFromImporting={driver_opted_out_from_importing}
              ownerOptedOutFromImporting={owner_opted_out_from_importing}
              contractorOptedOutFromImporting={contractor_opted_out_from_importing}
            />
          </div>
        )
      })
    ],
    [t, nbItems, leaseCompanyOptions, selectedLeaseCompanyOption, handleOnLeaseSelect]
  );

  return (
    <div className="table-container">
      <ReactTable
        isFetching={isLeadsCarsFetching}
        columns={columns}
        data={leadsCars?.items}
        pageCount={nbPages}
        pageSize={pageSize}
        totalItems={nbItems}
        pagination={nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
        overrideTableBodyMessage={
          !hasDateRangeSelected ? `${t("v8_please_select_at_least_one_date_range_to_filter_on", "Please select at least one date range to filter on")}.` : null
        }
        onPaginationChange={handlePaginationSelect}
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
